import React, { useEffect, useId, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, CheckCircle2, RotateCcw, X } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Notice } from './ui/Notice';
import type { Payment, RefundPaymentResponse } from '../types/payment.types';
import { useRefundPayment } from '../hooks/useRefundPayment';
import { formatCardNumber } from '../utils/paymentUtils';
import {
  FULL_ONLY_EXPLANATION,
  MAX_REASON_LENGTH,
  REFUND_OPERATION_LABEL,
  RefundConfirmation,
  RefundErrorInfo,
  amountIssueMessage,
  centsToDecimal,
  createRefundConfirmation,
  formatMoney,
  fullOnlyReason,
  mapRefundError,
  refundSummary,
  toCents,
  validateRefundAmount,
  validateReason,
} from '../utils/refunds';

/**
 * SAM-466 — refund (partial or full) of one payment from the Payments page.
 *
 * Form → explicit confirmation → result. Each confirmation carries its own idempotency key (see
 * createRefundConfirmation); "Retry" re-sends that same confirmation, "Back" throws it away.
 * Rendered above the payment drawer (which closes first — its focus lock would swallow typing).
 */

interface RefundPaymentDialogProps {
  isOpen: boolean;
  payment: Payment | null;
  /** The payment's business (the refund is authorized against it). */
  businessId: string | null;
  onClose: () => void;
}

/** Rodrigo (24 Oct): a TOTAL refund also cancels the pass of the payment (the server does it); a partial one does not. */
export const PASS_CANCELLED_WITH_REFUND = 'The pass of this payment will be cancelled too.';
export const refundCancelsPass = (payment: Payment, amountCents: number | null, remainingCents: number): boolean =>
  !!payment.business_access_id && amountCents !== null && remainingCents > 0 && amountCents === remainingCents;

type Terminal = { kind: 'error'; error: RefundErrorInfo } | { kind: 'success'; result: RefundPaymentResponse };

export const RefundPaymentDialog: React.FC<RefundPaymentDialogProps> = ({ isOpen, payment, businessId, onClose }) => {
  const titleId = useId();
  const mutation = useRefundPayment();
  const summary = useMemo(() => (payment ? refundSummary(payment) : null), [payment]);
  const remaining = summary?.remainingCents ?? 0;
  const currency = summary?.currency ?? 'DOP';

  const [learnedFullOnly, setLearnedFullOnly] = useState(false);
  const [amountInput, setAmountInput] = useState('');
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);
  const [formNotice, setFormNotice] = useState<RefundErrorInfo | null>(null);
  const [confirmation, setConfirmation] = useState<RefundConfirmation | null>(null);
  const [retryError, setRetryError] = useState<RefundErrorInfo | null>(null);
  const [terminal, setTerminal] = useState<Terminal | null>(null);

  // Fresh state for every payment the dialog is opened with.
  useEffect(() => {
    if (!isOpen) return;
    setLearnedFullOnly(false);
    setAmountInput(centsToDecimal(remaining));
    setReason('');
    setTouched(false);
    setFormNotice(null);
    setConfirmation(null);
    setRetryError(null);
    setTerminal(null);
    mutation.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, payment?.transaction_reference]);

  const pending = mutation.isPending;
  const close = () => {
    if (!pending) onClose();
  };

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  if (!isOpen || !payment || !summary) return null;

  const fullOnly = fullOnlyReason(payment, learnedFullOnly);
  const amountCheck = validateRefundAmount(fullOnly ? centsToDecimal(remaining) : amountInput, remaining, !!fullOnly);
  const reasonIssue = validateReason(reason);
  const canReview = amountCheck.ok && !reasonIssue && !!businessId && !!payment.transaction_reference;

  const review = () => {
    setTouched(true);
    if (!canReview || amountCheck.cents === null || !businessId || !payment.transaction_reference) return;
    setFormNotice(null);
    setRetryError(null);
    setConfirmation(
      createRefundConfirmation({
        transactionReference: payment.transaction_reference,
        businessId,
        amountCents: amountCheck.cents,
        remainingCents: remaining,
        reason,
      }),
    );
  };

  const send = (c: RefundConfirmation) => {
    setRetryError(null);
    mutation.mutate(c, {
      onSuccess: (result) => setTerminal({ kind: 'success', result }),
      onError: (err) => {
        const info = mapRefundError(err);
        switch (info.kind) {
          case 'retry':
            setRetryError(info); // stay on this confirmation; Retry re-sends the same key
            break;
          case 'full_only':
            setLearnedFullOnly(true);
            setAmountInput(centsToDecimal(remaining));
            setFormNotice(info);
            setConfirmation(null);
            break;
          case 'fix':
            setFormNotice(info);
            setConfirmation(null);
            break;
          default:
            setTerminal({ kind: 'error', error: info });
        }
      },
    });
  };

  const cardLabel = formatCardNumber(payment.cardLast4);
  const reference = payment.payment_reference || payment.transaction_reference || 'N/A';

  let body: React.ReactNode;
  let footer: React.ReactNode;
  let title = 'Refund payment';

  if (terminal?.kind === 'success') {
    const r = terminal.result;
    const refundedCents = toCents(r.refund.amount);
    const remainingAfter = toCents(r.payment.remaining_amount);
    const cur = r.refund.currency || currency;
    title = 'Refund completed';
    body = (
      <div className="flex flex-col gap-4" data-testid="refund-success">
        <div className="flex items-start gap-3">
          <CheckCircle2 size={22} className="shrink-0 text-ok-fg" />
          <div className="text-sm text-ink-700">
            <div className="font-semibold text-ink-900">
              {REFUND_OPERATION_LABEL[r.refund.operation] || 'Refund'} of {formatMoney(refundedCents, cur)} processed.
            </div>
            <div className="mt-1">
              Remaining on this payment: <strong>{formatMoney(remainingAfter, cur)}</strong>
            </div>
            {r.refund.gateway_reference && (
              <div className="mt-1 font-mono text-xs text-ink-500">Gateway ref: {r.refund.gateway_reference}</div>
            )}
          </div>
        </div>
        {r.replayed && (
          <Notice variant="info">This confirmation had already been processed; this is its original result. Nothing was refunded twice.</Notice>
        )}
      </div>
    );
    footer = <Button variant="primary" onClick={onClose}>Done</Button>;
  } else if (terminal?.kind === 'error') {
    const e = terminal.error;
    title = e.kind === 'no_retry' ? 'Refund needs reconciliation' : 'Refund not processed';
    body = (
      <div className="flex flex-col gap-3" data-testid="refund-terminal-error" data-code={e.code}>
        <Notice variant={e.kind === 'no_retry' ? 'warn' : 'error'}>
          <div className="font-semibold">{e.message}</div>
        </Notice>
        {e.kind === 'no_retry' && (
          <p className="m-0 text-xs text-ink-500">
            Reference {reference} · {formatMoney(confirmation?.amountCents ?? null, currency)}. Share this with
            finance or support; the payment list will show the outcome once it is reconciled.
          </p>
        )}
      </div>
    );
    footer = <Button variant="primary" onClick={onClose}>Close</Button>;
  } else if (confirmation) {
    title = 'Confirm refund';
    body = (
      <div className="flex flex-col gap-4" data-testid="refund-confirm">
        <p className="m-0 text-sm text-ink-700">
          You are about to refund <strong className="text-ink-900">{formatMoney(confirmation.amountCents, currency)}</strong>
          {confirmation.isFullRemaining ? ' (everything that remains)' : ''} to card <strong>{cardLabel}</strong>.
        </p>
        <dl className="m-0 grid grid-cols-[140px_1fr] gap-x-4 gap-y-2 rounded-lg border border-line bg-surface p-4 text-sm">
          <dt className="text-ink-500">Amount</dt>
          <dd className="m-0 font-semibold text-ink-900">{formatMoney(confirmation.amountCents, currency)}</dd>
          <dt className="text-ink-500">Card</dt>
          <dd className="m-0 text-ink-900">{cardLabel}</dd>
          <dt className="text-ink-500">Reference</dt>
          <dd className="m-0 break-all font-mono text-xs text-ink-900">{reference}</dd>
          <dt className="text-ink-500">Reason</dt>
          <dd className="m-0 whitespace-pre-wrap break-words text-ink-900">{confirmation.request.reason}</dd>
        </dl>
        {refundCancelsPass(payment, confirmation.amountCents, remaining) && (
          <Notice variant="warn" data-testid="refund-confirm-cancels-pass">{PASS_CANCELLED_WITH_REFUND}</Notice>
        )}
        {retryError ? (
          <Notice variant="error" data-testid="refund-retry-error">{retryError.message}</Notice>
        ) : (
          <Notice variant="warn">A refund cannot be undone.</Notice>
        )}
      </div>
    );
    footer = (
      <>
        <Button variant="ghost" onClick={() => setConfirmation(null)} disabled={pending} icon={<ArrowLeft size={14} />}>
          Back
        </Button>
        <Button
          variant="danger"
          onClick={() => send(confirmation)}
          isLoading={pending}
          icon={retryError ? <RotateCcw size={14} /> : undefined}
          data-testid="refund-submit"
        >
          {retryError ? 'Retry refund' : `Refund ${formatMoney(confirmation.amountCents, currency)}`}
        </Button>
      </>
    );
  } else {
    const amountError =
      touched && !amountCheck.ok && amountCheck.issue && !fullOnly
        ? amountIssueMessage(amountCheck.issue, remaining, currency)
        : undefined;
    const reasonError =
      touched && reasonIssue
        ? reasonIssue === 'required'
          ? 'A reason is required.'
          : `At most ${MAX_REASON_LENGTH} characters.`
        : undefined;
    body = (
      <div className="flex flex-col gap-4" data-testid="refund-form">
        <dl className="m-0 grid grid-cols-3 gap-3 text-sm">
          <div className="rounded-lg border border-line bg-surface p-3">
            <dt className="text-xs text-ink-500">Paid</dt>
            <dd className="m-0 font-semibold text-ink-900">{formatMoney(summary.paidCents, currency)}</dd>
          </div>
          <div className="rounded-lg border border-line bg-surface p-3">
            <dt className="text-xs text-ink-500">Refunded</dt>
            <dd className="m-0 font-semibold text-ink-900">{formatMoney(summary.refundedCents, currency)}</dd>
          </div>
          <div className="rounded-lg border border-line bg-surface p-3">
            <dt className="text-xs text-ink-500">Remaining</dt>
            <dd className="m-0 font-semibold text-ink-900" data-testid="refund-remaining">{formatMoney(remaining, currency)}</dd>
          </div>
        </dl>

        {formNotice && (
          <Notice variant={formNotice.kind === 'full_only' ? 'warn' : 'error'} data-testid="refund-form-notice">
            {formNotice.message}
          </Notice>
        )}

        <Input
          label={`Amount to refund (${currency})`}
          required
          inputMode="decimal"
          value={fullOnly ? centsToDecimal(remaining) : amountInput}
          onChange={(e) => setAmountInput(e.currentTarget.value)}
          disabled={!!fullOnly}
          error={amountError}
          hint={fullOnly ? undefined : `Up to ${formatMoney(remaining, currency)}. Defaults to the full remaining amount.`}
          data-testid="refund-amount"
        />
        {fullOnly && <Notice variant="info" data-testid="refund-full-only">{FULL_ONLY_EXPLANATION[fullOnly]}</Notice>}
        {refundCancelsPass(payment, amountCheck.ok ? amountCheck.cents : null, remaining) && (
          <Notice variant="warn" data-testid="refund-cancels-pass">{PASS_CANCELLED_WITH_REFUND}</Notice>
        )}

        <div className={`fc ${reasonError ? 'err' : ''}`.trim()}>
          <label htmlFor={`${titleId}-reason`}>
            Reason<span className="req">*</span>
          </label>
          <textarea
            id={`${titleId}-reason`}
            value={reason}
            maxLength={MAX_REASON_LENGTH}
            rows={3}
            placeholder="Why is this payment being refunded?"
            onChange={(e) => setReason(e.currentTarget.value)}
            data-testid="refund-reason"
          />
          {reasonError ? (
            <div className="msg">{reasonError}</div>
          ) : (
            <div className="cap">{reason.trim().length}/{MAX_REASON_LENGTH}. Kept with the refund record.</div>
          )}
        </div>
      </div>
    );
    footer = (
      <>
        <Button variant="ghost" onClick={close}>Cancel</Button>
        <Button variant="primary" onClick={review} disabled={touched && !canReview} data-testid="refund-review">
          Review refund
        </Button>
      </>
    );
  }

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/30 p-5"
      style={{ zIndex: 20000 }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="flex max-h-[90vh] w-full max-w-[520px] flex-col overflow-hidden rounded-xl bg-white shadow-xl"
        data-testid="refund-dialog"
      >
        <div className="flex items-start justify-between gap-3 border-b border-line-subtle px-6 pb-4 pt-5">
          <div>
            <div id={titleId} className="text-lg font-semibold text-ink-900">{title}</div>
            <div className="mt-0.5 text-[13px] text-ink-500">
              {cardLabel} · <span className="font-mono">{reference}</span>
            </div>
          </div>
          <button type="button" className="bk-icon-btn" onClick={close} disabled={pending} aria-label="Close">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{body}</div>
        <div className="flex justify-end gap-3 border-t border-line-subtle px-6 py-4">{footer}</div>
      </div>
    </div>,
    document.body,
  );
};
